import { useState } from 'react'

import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useMemo } from 'modules/core/hooks/useMemo'
import { stringToDate } from 'modules/core/lib/dates'

import { DaysByRow } from 'modules/shift_management/hooks/reducers/selection'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import ShiftForm from 'modules/shift_management/lib/forms/ShiftForm'
import {
  onAddPlannedBreak,
  onDeletePlannedBreak,
} from 'modules/shift_management/lib/managePlannedBreaks'

const useCustomShiftForm = ({
  daysByEmployee,
  isTimeFormat24H,
}: {
  daysByEmployee: DaysByRow
  isTimeFormat24H: boolean
}) => {
  const [{ view }] = useSchedule()
  const { isEnabled: hasSelection } = useSelectionState()
  const [plannedBreakForms, setPlannedBreakForms] = useState<PlannedBreakForm[]>([])

  const selectedTimes = Object.values(daysByEmployee)
    .flat()
    .map((date) => stringToDate(date, view.dateFormat))
  const hasDefaultTimes = view.isDaily && hasSelection
  const startTime = hasDefaultTimes ? moment.min(selectedTimes).format('HH:mm') : ''
  const endTime = hasDefaultTimes ? moment.max(selectedTimes).add('1', 'h').format('HH:mm') : ''

  const customShiftForm = useMemo(
    () =>
      new ShiftForm({
        start_time: startTime,
        end_time: endTime,
        extra_hours: false,
      }),
    [startTime, endTime]
  )

  const onAddBreak = (breakConfiguration: BreakConfigurationFragment, breakType: BreakType) => {
    onAddPlannedBreak(
      breakConfiguration,
      breakType,
      plannedBreakForms,
      isTimeFormat24H,
      (value) => {
        setPlannedBreakForms(value)
      }
    )
  }

  const onDeleteBreak = (breakForm: PlannedBreakForm) => {
    onDeletePlannedBreak(breakForm, plannedBreakForms, setPlannedBreakForms)
  }

  return {
    customShiftForm,
    onAddBreak,
    onDeleteBreak,
    plannedBreakForms,
  }
}

export default useCustomShiftForm
